// The region of a pick: a block of squares around the square the caster
// picks. The whole block is on the board. A pick at an edge takes the block
// that touches that edge, so the picked square is not always the middle.

import { BOARD_SIZE, REGION_SIDE } from '../constants';
import { fileOf, rankOf, squareIndex } from '../engine/squares';
import type { GameState } from '../game/state';
import type { PickRegion } from './types';

/** The squares from the middle of a region to its edge. */
const REGION_REACH = (REGION_SIDE - 1) / 2;

/** The file or rank nearest to this one that can be the middle of a region. */
const middleNear = (line: number): number =>
  Math.min(Math.max(line, REGION_REACH), BOARD_SIZE - 1 - REGION_REACH);

/** The middle of the region a pick of this square takes. */
export const regionMiddle = (square: number): number =>
  squareIndex(middleNear(fileOf(square)), middleNear(rankOf(square)));

/** The squares of the region a pick of this square takes, row by row. */
export const regionSquares = (square: number): number[] => {
  const middle = regionMiddle(square);
  const offsets = Array.from({ length: REGION_SIDE }, (_, index) => index - REGION_REACH);
  return offsets.flatMap((rankStep) =>
    offsets.map((fileStep) => squareIndex(fileOf(middle) + fileStep, rankOf(middle) + rankStep)),
  );
};

/** The region of a pick, with its squares that have no piece and no terrain. */
export const openRegion = (state: GameState, square: number): PickRegion => {
  const squares = regionSquares(square);
  return {
    middle: regionMiddle(square),
    squares,
    open: squares.filter((each) => state.board[each] === null && !state.rules.terrain[each]),
  };
};
